import { useEffect, useRef } from 'react';
import type { BookingState } from '../state/machine';
import { buildBookingDetails, createBookingDetailsReporter } from './booking-details';

/**
 * Report the customer's booking details to the middleware as they change.
 *
 * `active` is "step 1 is done and the booking is not". Before step 1 there
 * is no funnel row to write to — and a preset can select a studio or a
 * fitting before the customer has said who they are. After the confirmation
 * the row is complete and is left alone.
 *
 * Kept out of widget.tsx on purpose: this is one hook call there, so the
 * reporting rules live and are tested here.
 */
export function useBookingDetailsReport(state: BookingState, sessionId: string | undefined, active: boolean): void {
  const reporter = useRef(createBookingDetailsReporter()).current;

  // Rescheduling moves an existing appointment; it is not a booking attempt
  // and has no funnel row of its own.
  const rescheduling = Boolean((state as { rescheduleMode?: boolean }).rescheduleMode);
  const on = active && !rescheduling && !state.confirmation;

  // Each time step 1 completes it rewrites the row's status to "started", so
  // whatever is already chosen is sent again rather than assumed known.
  useEffect(() => {
    if (on) reporter.reset();
    else reporter.cancel();
  }, [on, reporter]);

  const snapshot = on ? buildBookingDetails(state, sessionId) : null;
  const key = snapshot ? JSON.stringify(snapshot) : '';
  useEffect(() => {
    if (snapshot) reporter.update(snapshot);
    // `key` is the snapshot by value; the object itself is new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, reporter]);

  // Book was clicked: send what is waiting now rather than after the pause.
  // If it lands after the confirmation, the middleware ignores it.
  useEffect(() => {
    if (state.submitting) reporter.flush();
  }, [state.submitting, reporter]);

  // Leaving the page is the moment that matters most for an abandoned
  // booking, so the last change is not left sitting in the pause.
  useEffect(() => {
    const onHide = () => reporter.flush();
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      reporter.flush();
    };
  }, [reporter]);
}
